import { EImportDestination } from "api/enums";
import { IImportationField, IImportationParameters } from "api/interfaces";
import { IImportationXLSXData } from "api/interfaces/IImportationXLSXData";
import ContractImportService from "api/services/ContractImportService";
import ExcelService from "api/services/ExcelService";
import { makeAutoObservable } from "mobx";
import {
  ContractRowStore,
  ContractStore,
  MeasurementRowsStore,
  MeasurementStore,
  UserStore,
} from "..";
import { lang } from "../../i18nLang";
import { keyNameNormalize } from "../../utils";

class Store {
  public file: File | null = null;
  public fields: { [name: string]: string } = {};
  public customFields: { [name: string]: { field: string; value: string } } =
    {};
  public parameters: IImportationParameters = { tabIndex: 0, startRow: 2 };
  public fileTabs: { name: string; id: number }[] = [];
  public fieldsMeasurement = { codeWBS: "", measure: "", measureBy: "" };

  public processing: boolean = false;
  public importStatus: string = "preparing";
  public importProgress: number = 0;
  public importTimeLeft: string = "";

  public error: { message: string; detail: string | string[] } = {
    message: "",
    detail: "",
  };

  public readonly validColumns = {
    required: {},
    match: {
      regex: /[A-Z]+/,
    },
  };

  public readonly validations = {
    codeWBS: this.validColumns,
    description: this.validColumns,
    quantity: this.validColumns,
    unit: this.validColumns,
    unitCost: this.validColumns,
  };

  constructor() {
    this.reset();
    makeAutoObservable(this);
  }

  reset() {
    this.fields = {
      codeWBS: "",
      description: "",
      quantity: "",
      unit: "",
      unitCost: "",
    };
    this.parameters = { tabIndex: 0, startRow: 2 };
    this.fieldsMeasurement = { codeWBS: "", measure: "", measureBy: "" };
    this.customFields = {};
    this.file = null;
    this.fileTabs = [];
    this.processing = false;
    this.importStatus = "preparing";
    this.importProgress = 0;
    this.importTimeLeft = "";
    this.error = { message: "", detail: "" };
  }

  getFieldsNames() {
    return Object.keys(this.fields);
  }

  allFieldsAreFilled() {
    return this.getFieldsNames().every((field) => this.fields[field]);
  }

  isFileUploaded(): boolean {
    return this.importStatus === "file_uploaded";
  }

  importationHasError() {
    return Boolean(this.error.message);
  }

  contractIsEmpty() {
    return ContractRowStore.rowsCount === 0;
  }

  setProcessing(processing: boolean) {
    this.processing = processing;
  }

  setFields(field: string, value: string) {
    if (!field || value === undefined) {
      return;
    }

    let newFields = { ...this.fields };
    newFields[field] = value.toUpperCase();

    this.fields = { ...newFields };
  }

  setCustomField(key: string, field: string, value: string) {
    if (!key || !field || value === undefined) {
      return;
    }

    let newCustomFields = { ...this.customFields };
    newCustomFields[key] = {
      field,
      value,
    };

    this.customFields = { ...newCustomFields };
  }

  removeCustomField(key: string) {
    let newCustomFields = { ...this.customFields };
    delete newCustomFields[key];
    this.customFields = newCustomFields;
  }

  setFile(file: File) {
    this.file = file;
  }

  async setFileTabs(file: File) {
    const service = new ExcelService();
    const worksheets = await service.readTabsFromFile(file);

    const tabs = worksheets.map((w, index) => ({ name: w.name, id: index }));
    this.fileTabs = tabs;
  }

  setParameter(property: string, value: number) {
    const newParameters = { ...this.parameters };
    newParameters[property] = value;
    this.parameters = newParameters;
  }

  setFieldsMeasurement(field: string, value: string) {
    if (!field || value === undefined) {
      return;
    }

    let newFields = { ...this.fieldsMeasurement };
    newFields[field] = value;

    this.fieldsMeasurement = { ...newFields };
  }

  async startContractImportation(
    isAdditive: boolean,
    attachmentsIds?: string[]
  ): Promise<void> {
    const contractId = ContractStore.getCurrentContract()?.uid || "";
    const user = UserStore.currentUser;
    const allFields = this.getFieldsToImportation();
    const destination = isAdditive
      ? EImportDestination.ADDITIVES
      : EImportDestination.CONTRACT;

    if (!contractId || !user || !this.file) return;

    this.parameters.user = user;
    this.parameters.attachmentsIds = attachmentsIds;

    this.processing = true;
    return ContractImportService.importContractXLSX(
      contractId,
      destination,
      allFields,
      this.file,
      this.parameters,
      (newPath) => this.setStatusToUploaded(newPath),
      (error) => this.setUploadError(error),
      (data) => this.onProgressImportation(data)
    )
      .then(() => {
        this.setImportStatus("uploading");
      })
      .catch((error) => {
        console.error(error);
        this.setError("errorOnSchedule");
      });
  }

  async startMeasurementImportation(): Promise<void> {
    const contractId = ContractStore.getCurrentContract()?.uid || "";
    const measurementId = MeasurementStore.currentMeasurement?.uid || "";
    const user = UserStore.currentUser;
    const allFields = this.getMeasurementFieldsToImportation();
    const destination = EImportDestination.MEASUREMENT;

    if (!contractId || !measurementId || !user || !this.file) return;

    this.parameters.user = user;
    this.parameters.measurementId = measurementId;

    this.processing = true;
    return ContractImportService.importContractXLSX(
      contractId,
      destination,
      allFields,
      this.file,
      this.parameters,
      (newPath) => this.setStatusToUploaded(newPath),
      (error) => this.setUploadError(error),
      (data) => this.onProgressImportation(data, measurementId)
    )
      .then(() => {
        this.setImportStatus("uploading");
      })
      .catch((error) => {
        console.error(error);
        this.setError("errorOnSchedule");
      });
  }

  private setStatusToUploaded(newPath: string) {
    if (newPath) {
      this.importStatus = "file_uploaded";
    }
  }

  private setUploadError(error) {
    console.error(error);
    this.setError("errorOnSchedule", lang(`importation.status.upload_error`));
    this.importStatus = "upload_error";
  }

  public setAttachmentError() {
    this.setError("errorOnSchedule", lang(`attachments.errors.saveError`));
  }

  private onProgressImportation(
    data: IImportationXLSXData,
    measurementId?: string
  ) {
    this.setImportStatus(data.status, data.progress);
    this.setImportProgress(data.progress);
    this.setImportTimeLeft(data.progress, data.start);

    if (data.status === "error") {
      this.setError(
        "errorOnProcess",
        data.error?.messageTranslated.split("\n")
      );
    }

    if (data.status === "processed" && data.progress === 100) {
      ContractRowStore.refreshContractRows();
      if (measurementId) {
        MeasurementRowsStore.reset();
        MeasurementStore.subscribeMeasurement(measurementId);
      }
    }
  }

  private getFieldsToImportation(): IImportationField[] {
    const fields = this.getFieldsNames().map((field) => ({
      name: field,
      column: this.fields[field],
    }));

    const customFieldsKeys = Object.keys(this.customFields);
    const customFields = customFieldsKeys.map((key) => {
      const { field, value } = this.customFields[key];
      return {
        name: keyNameNormalize(field),
        label: field,
        column: value,
      };
    });

    return [...fields, ...customFields].filter(
      (field) => field.name && field.column
    );
  }

  private getMeasurementFieldsToImportation(): IImportationField[] {
    return [
      {
        name: "codeWBS",
        column: this.fieldsMeasurement.codeWBS,
      },
      {
        name: this.fieldsMeasurement.measureBy,
        column: this.fieldsMeasurement.measure,
      },
    ];
  }

  private setImportStatus(status: string = "", progress: number = 0) {
    if (status && status !== "schedule") {
      const isProcessing = status === "processed" && progress < 100;
      this.importStatus = isProcessing ? "processed data" : status;
    }
  }

  private setImportProgress(progress: number = 0) {
    if (progress && progress > this.importProgress)
      this.importProgress = Math.round(progress);
  }

  private setImportTimeLeft(progress: number = 0, start?: Date) {
    const estimatedTime = this.calculateEstimatedTime(progress, start);
    const labelEstimatedTime = this.getEstimatedTimeLabel(
      estimatedTime,
      progress >= 75
    );

    if (this.importTimeLeft !== labelEstimatedTime) {
      this.importTimeLeft = labelEstimatedTime; // Para evitar muitas renderizações
    }
  }

  private getEstimatedTimeLabel(
    estimatedTime: number,
    showUntilText: boolean = false
  ) {
    return estimatedTime > 60
      ? `${Math.ceil(estimatedTime / 60)} m`
      : (showUntilText ? "até " : "") + `${estimatedTime} s`;
  }

  private calculateEstimatedTime(progress: number = 0, start?: Date): number {
    if (!start) return 0;
    progress = progress < 1 ? 1 : progress;

    const now: any = new Date();
    const diffTime = Math.abs(now - (start as any));
    const estimatedTotal = Math.ceil((diffTime * 100) / progress);

    const final: any = new Date(start.getTime() + estimatedTotal);
    const diffSeconds = Math.abs(final - now) / 1000;
    return Math.ceil(diffSeconds);
  }

  private setError(type: string, detail: string | string[] = "") {
    this.error = {
      message: lang(`importation.${type}`),
      detail,
    };
  }
}

const ContractImportationStore = new Store();
export default ContractImportationStore;
